import { useEffect, useLayoutEffect, useRef, useState } from "react";

const reduced = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

// Rolling-word pill (patterns/glass-motion.css §3). Only true options; screen
// readers get the full list; reduced motion shows the first option still.
export function Morph({ words, interval = 2600 }: { words: string[]; interval?: number }) {
  const [i, setI] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const sizer = useRef<HTMLSpanElement>(null);
  const [w, setW] = useState<number>();
  useEffect(() => {
    if (reduced() || words.length < 2) return;
    const t = setInterval(() => setI((x) => { setPrev(x); return (x + 1) % words.length; }), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);
  useLayoutEffect(() => { if (sizer.current) setW(sizer.current.offsetWidth); }, [i]);
  return (
    <>
      <span className="sr-only">{words.join(", ")}</span>
      <span className="pm-morph" aria-hidden="true" style={w ? { width: w } : undefined}>
        <span ref={sizer} className="pm-morph-word pm-morph-sizer" style={{ visibility: "hidden" }}>{words[i]}</span>
        {prev !== null && <span key={`o${prev}-${i}`} className="pm-morph-word out">{words[prev]}</span>}
        <span key={`i${i}`} className={`pm-morph-word ${prev === null ? "" : "in"}`} style={prev === null ? { position: "absolute", left: "var(--pad)", top: "50%", transform: "translateY(-50%)" } : undefined}>{words[i]}</span>
      </span>
    </>
  );
}

// Count-up on first view (expressive tier). The DOM always ships the final value.
export function CountUp({ value, duration = 1100 }: { value: number; duration?: number }) {
  const [shown, setShown] = useState(reduced() ? value : 0);
  const done = useRef(false);
  useEffect(() => {
    if (reduced() || done.current) { setShown(value); return; }
    done.current = true;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span aria-label={String(value)}>{shown.toLocaleString()}</span>;
}

// Pop a value when it changes (e.g. a count after an action).
export function Pop({ children, watch }: { children: React.ReactNode; watch: unknown }) {
  const ref = useRef<HTMLSpanElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const el = ref.current;
    if (!el) return;
    el.classList.remove("pm-pop");
    void el.offsetWidth;
    el.classList.add("pm-pop");
  }, [watch]);
  return <span ref={ref} style={{ display: "inline-block" }}>{children}</span>;
}
